import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { school } from "../mock/mock";
import { getConsent, CONSENT_EVENT } from "../lib/consent";
import { CAMPAIGN } from "../lib/campaign";
import CopyCode from "./CopyCode";

const SEEN_KEY = "ct_campaign_host2026_v2";
const ENDS = CAMPAIGN.end;

export default function CampaignPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [consentDecided, setConsentDecided] = useState(() => getConsent().decided);

  useEffect(() => {
    const h = (e) => setConsentDecided(e.detail.decided);
    window.addEventListener(CONSENT_EVENT, h);
    return () => window.removeEventListener(CONSENT_EVENT, h);
  }, []);

  useEffect(() => {
    if (pathname !== "/" || !consentDecided || new Date() > ENDS || sessionStorage.getItem(SEEN_KEY)) return;
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, [pathname, consentDecided]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => { sessionStorage.setItem(SEEN_KEY, "1"); setOpen(false); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="campaign-title" data-testid="campaign-popup">
      <div className="absolute inset-0 bg-[#0B2545]/70 backdrop-blur-sm campaign-fade" onClick={close} />
      <div className="relative w-full max-w-5xl max-h-[92dvh] overflow-y-auto bg-white rounded-3xl shadow-2xl shadow-black/40 ring-1 ring-white/20 campaign-pop grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] pt-12 sm:pt-0">
        <button onClick={close} aria-label="Stäng" data-testid="campaign-close" className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-white/90 text-[#0B2545] shadow-md flex items-center justify-center hover:bg-[#ffce3a] transition-colors">
          <X size={18} />
        </button>

        <div className="relative bg-[#d3cec4] sm:pb-[100%] sm:rounded-l-3xl overflow-hidden">
          <img src="/img/hostkampanj.webp" alt="Höstkampanj – 10 % rabatt på alla paket med körlektioner 1 oktober – 30 november" className="block w-full h-auto sm:absolute sm:inset-0 sm:h-full sm:object-cover" data-testid="campaign-image" />
        </div>

        <div className="p-7 sm:p-9 flex flex-col justify-center">
          <span className="inline-flex self-start items-center gap-2 px-3 py-1.5 rounded-full bg-[#ffce3a] text-[#0B2545] text-[11px] font-extrabold tracking-[0.2em] uppercase">Höstkampanj</span>
          <h2 id="campaign-title" className="font-display text-3xl sm:text-4xl font-extrabold text-[#0B2545] mt-4 leading-[1.05]">
            <span className="text-5xl sm:text-6xl block">10 %</span> rabatt på alla paket med körlektioner
          </h2>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Gäller <strong className="text-[#0B2545]">{CAMPAIGN.periodText}</strong> på alla start-, kör- och intensivpaket. Uppge rabattkoden vid köp online så dras rabatten direkt.
          </p>
          <CopyCode className="mt-4" />
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href={school.bookingUrl} target="_blank" rel="noopener noreferrer" onClick={close} data-testid="campaign-book-btn" className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#0B2545] text-white font-bold hover:bg-[#13315C] transition-colors">
              Boka online <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <Link to="/priser" onClick={close} data-testid="campaign-prices-link" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-slate-200 text-[#0B2545] font-bold hover:bg-[#f6f9fc] transition-colors">
              Se alla paket
            </Link>
          </div>
          <p className="text-[11px] text-slate-400 mt-5">{CAMPAIGN.terms}</p>
        </div>
      </div>
    </div>
  );
}
